// frontend/src/components/shared/CaseQuickActions.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  Mail,
  FileText,
  FolderArchive,
  Phone,
  History,
  Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import { logActivity } from '../../api';
import { useApp, ROLES } from '../../context/AppContext';
import { generatePrintableCaseReport } from '../../utils/printReport';
import { CaseShareModal } from './CaseShareModal';
import { CaseCallModal } from './CaseCallModal';

export const CaseQuickActions = ({ caseData, className = '' }) => {
  const navigate = useNavigate();
  const { currentRole, currentUserId, currentUser } = useApp();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);

  if (!caseData) return null;

  const origin = window.location.origin;
  const caseUrl = `${origin}/cases/${caseData.id}`;
  const clientName = caseData.client?.name || caseData.clientName || 'N/A';
  const productName = caseData.product?.name || caseData.productName || caseData.verificationType || 'Address Verification';
  const userName = currentUser?.name || 'User';

  const defaultPhone = caseData.applicantPhone || '';
  const cleanPhone = defaultPhone.replace(/[^0-9]/g, '');

  const whatsappMessage = `🏢 *AD VENTURES - CASE VERIFICATION*
━━━━━━━━━━━━━━━━━━━━
📋 *Case Ref:* ${caseData.refNumber}
👤 *Applicant:* ${caseData.applicantName}
📞 *Phone:* ${caseData.applicantPhone || 'N/A'}
📍 *Address:* ${caseData.address || 'N/A'}
🏛️ *Client:* ${clientName}
🔍 *Verification:* ${productName}
📊 *Status:* ${(caseData.status || 'Active').toUpperCase()}
📅 *Due Date:* ${caseData.dueDate ? new Date(caseData.dueDate).toLocaleDateString('en-IN') : 'N/A'}

🔗 *View Case Online:*
${caseUrl}`;

  // 1. WhatsApp Action
  const handleWhatsApp = (e) => {
    e.stopPropagation();
    const waUrl = cleanPhone
      ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(whatsappMessage)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(whatsappMessage)}`;

    window.open(waUrl, '_blank', 'noopener,noreferrer');
    toast.success(`WhatsApp opened for case ${caseData.refNumber}!`);

    logActivity({
      action: 'WhatsApp Sent',
      description: `${userName} sent case ${caseData.refNumber} details via WhatsApp to ${caseData.applicantName} (${caseData.applicantPhone || 'Applicant'})`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  // 2. Email Action
  const handleEmail = (e) => {
    e.stopPropagation();
    const subject = `Case Verification Details - ${caseData.refNumber} (${caseData.applicantName})`;
    const body = `Dear ${caseData.applicantName || 'Applicant'},

Here are the verification details for Case Reference: ${caseData.refNumber}

Applicant Name: ${caseData.applicantName}
Address: ${caseData.address || 'N/A'}
Client: ${clientName}
Verification Type: ${productName}
Status: ${(caseData.status || 'Active').toUpperCase()}
Due Date: ${caseData.dueDate ? new Date(caseData.dueDate).toLocaleDateString('en-IN') : 'N/A'}

View complete case online:
${caseUrl}

Best regards,
Ad Ventures Operations`;

    const mailtoUrl = `mailto:${caseData.applicantEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    toast.success(`Email client opened for ${caseData.refNumber}`);

    logActivity({
      action: 'Email Sent',
      description: `${userName} opened email for case ${caseData.refNumber} to ${caseData.applicantEmail || 'Applicant'}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  // 3. PDF Download Action
  const handlePdf = (e) => {
    e.stopPropagation();
    generatePrintableCaseReport(caseData);
    toast.success(`PDF report generated for ${caseData.refNumber}!`);

    logActivity({
      action: 'PDF Report Generated',
      description: `${userName} generated and downloaded PDF report for case ${caseData.refNumber}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  // 4. ZIP Download Action
  const handleZip = (e) => {
    e.stopPropagation();
    toast.success(`Preparing document ZIP archive for ${caseData.refNumber}...`);

    logActivity({
      action: 'ZIP Package Downloaded',
      description: `${userName} requested documents ZIP package for case ${caseData.refNumber}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });

    // Provide instant mock archive download
    const blob = new Blob([
      `Case Reference: ${caseData.refNumber}\nApplicant: ${caseData.applicantName}\nAddress: ${caseData.address}\nStatus: ${caseData.status}`
    ], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${caseData.refNumber}_documents.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 5. Call Logs Action
  const handleCall = (e) => {
    e.stopPropagation();
    setCallModalOpen(true);
  };

  // 6. Share Action
  const handleShare = (e) => {
    e.stopPropagation();
    setShareModalOpen(true);
  };

  // 7. Case History Action
  const handleHistory = (e) => {
    e.stopPropagation();
    navigate(`/cases/${caseData.id}/history`);
  };

  const showHistory = [
    ROLES.ADMIN,
    ROLES.SUPREME_MANAGER,
    ROLES.MANAGER,
    ROLES.VENDOR,
    ROLES.QUALITY_CHECKER,
    ROLES.UNIVERSAL_QC,
  ].includes(currentRole);

  return (
    <>
      <div className={`flex items-center gap-1.5 justify-end ${className}`}>
        {/* WhatsApp */}
        <div className="relative group">
          <button
            onClick={handleWhatsApp}
            aria-label="Send to WhatsApp"
            className="w-7 h-7 rounded-full flex items-center justify-center border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:scale-105 transition-all shadow-2xs"
          >
            <MessageCircle className="w-3.5 h-3.5" />
          </button>
          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 scale-0 group-hover:scale-100 transition-all rounded bg-slate-900 px-2 py-1 text-[9px] font-bold text-white shadow-xs z-30 w-max">
            Send to WhatsApp
          </span>
        </div>

        {/* Email */}
        <div className="relative group">
          <button
            onClick={handleEmail}
            aria-label="Send to Email"
            className="w-7 h-7 rounded-full flex items-center justify-center border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:scale-105 transition-all shadow-2xs"
          >
            <Mail className="w-3.5 h-3.5" />
          </button>
          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 scale-0 group-hover:scale-100 transition-all rounded bg-slate-900 px-2 py-1 text-[9px] font-bold text-white shadow-xs z-30 w-max">
            Send to Email
          </span>
        </div>

        {/* PDF */}
        <div className="relative group">
          <button
            onClick={handlePdf}
            aria-label="Download PDF Report"
            className="w-7 h-7 rounded-full flex items-center justify-center border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:scale-105 transition-all shadow-2xs"
          >
            <FileText className="w-3.5 h-3.5" />
          </button>
          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 scale-0 group-hover:scale-100 transition-all rounded bg-slate-900 px-2 py-1 text-[9px] font-bold text-white shadow-xs z-30 w-max">
            Download PDF Report
          </span>
        </div>

        {/* ZIP */}
        <div className="relative group">
          <button
            onClick={handleZip}
            aria-label="Download ZIP Archive"
            className="w-7 h-7 rounded-full flex items-center justify-center border border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100 hover:scale-105 transition-all shadow-2xs"
          >
            <FolderArchive className="w-3.5 h-3.5" />
          </button>
          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 scale-0 group-hover:scale-100 transition-all rounded bg-slate-900 px-2 py-1 text-[9px] font-bold text-white shadow-xs z-30 w-max">
            Download ZIP
          </span>
        </div>

        {/* Call Logs */}
        <div className="relative group">
          <button
            onClick={handleCall}
            aria-label="Call Applicant / Call Logs"
            className="w-7 h-7 rounded-full flex items-center justify-center border border-teal-200 bg-teal-50 text-teal-600 hover:bg-teal-100 hover:scale-105 transition-all shadow-2xs"
          >
            <Phone className="w-3.5 h-3.5" />
          </button>
          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 scale-0 group-hover:scale-100 transition-all rounded bg-slate-900 px-2 py-1 text-[9px] font-bold text-white shadow-xs z-30 w-max">
            Call Applicant & Logs
          </span>
        </div>

        {/* Share Case & Copy Link */}
        <div className="relative group">
          <button
            onClick={handleShare}
            aria-label="Share Case & Copy Link"
            className="w-7 h-7 rounded-full flex items-center justify-center border border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:scale-105 transition-all shadow-2xs"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 scale-0 group-hover:scale-100 transition-all rounded bg-slate-900 px-2 py-1 text-[9px] font-bold text-white shadow-xs z-30 w-max">
            Share Case & Copy URL
          </span>
        </div>

        {/* Case History */}
        {showHistory && (
          <div className="relative group">
            <button
              onClick={handleHistory}
              aria-label="View Complete Case History"
              className="w-7 h-7 rounded-full flex items-center justify-center border border-purple-200 bg-purple-50 text-purple-600 hover:bg-purple-100 hover:scale-105 transition-all shadow-2xs"
            >
              <History className="w-3.5 h-3.5" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 scale-0 group-hover:scale-100 transition-all rounded bg-slate-900 px-2 py-1 text-[9px] font-bold text-white shadow-xs z-30 w-max">
              View Complete Case History
            </span>
          </div>
        )}
      </div>

      {/* Share Modal */}
      <CaseShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        caseData={caseData}
      />

      {/* Call Modal */}
      <CaseCallModal
        open={callModalOpen}
        onOpenChange={setCallModalOpen}
        caseData={caseData}
      />
    </>
  );
};
